import Link from "next/link"
import React, { useEffect, useMemo, useState } from "react"
import { MobileNavigation } from "./MobileNavigation"
import { NavigationSubItems } from "./NavigationSubItems"
import { NavigationItem } from "./NavigationItem"
import { NavigationLogo } from "./NavigationLogo"
import { headerDesktopLinks } from "@layouts/NavigationV2/navigation/headerDesktopLinks"
import { headerDesktopLinksShort} from "@layouts/NavigationV2/navigation/headerDesktopLinksShort"
import { twJoin } from "@library/tailwindMerge"
import useUtmTags, {Utm} from "@hooks/useUtmTags"
import { MainButton } from "@components/MainButton/MainButton"
import Icon from "@components/UI/Icon"

export type MenuMode = "full" | "short"

export type NavigationProps = {
  staticTheme: string
  actionTheme: string
  background?: string
  mode?: MenuMode
}

export const Navigation = ({ staticTheme, actionTheme, background, mode = 'full' }: NavigationProps) => {
  const [
    state,
    setState,
  ] = useState({
    activeType: undefined,
    isSticky: false,
  })

  const isSticky = () => {
    setState((prevState) => ({
      ...prevState,
      isSticky: window.scrollY >= 1,
    }))
  }

  useEffect(() => {
    isSticky()
    
    window.addEventListener("scroll", isSticky)

    return () => {
      window.removeEventListener("scroll", isSticky)
    }
  }, [])

  const getTheme = useMemo(() => {
    if (typeof state.activeType !== "undefined") {
      return actionTheme
    }

    if (state.isSticky) {
      return actionTheme
    }

    return staticTheme
  }, [
    actionTheme,
    state.activeType,
    state.isSticky,
    staticTheme,
  ])
  
  const linkSource = mode === "short" ? headerDesktopLinksShort : headerDesktopLinks

  return (
    <nav
      className={twJoin(
          "navbar",
         actionTheme === "neutral" ? `${background??"bg-neutral-900"}` : "",
        actionTheme === "dark" ? "fixed" : "sticky",
        "top-0 z-50 w-full",
        "p-16p",
        "transition-colors",
        getTheme,
        state.isSticky || state.activeType
          ? [
              "[&.light]:bg-white",
              "[&.dark]:bg-neutral2-900",
            ]
          : ""
      )}
    >
      <div className={"w-full max-w-[1344px] mx-auto relative"}>
        <div
          className={twJoin(
            "relative",
            "w-full max-w-screen-1280 mx-auto",
            "flex items-center justify-between gap-x-16p"
          )}
        >
          <div className={twJoin("flex items-center justify-between gap-x-48p")}>
            <NavigationLogo
              href="/"
              theme={getTheme}
            />

            <ul className={twJoin("flex gap-x-32p", "hidden", "1440:flex")}>
              {linkSource.map((item) => (
                <NavigationItem
                  key={item.type}
                  theme={getTheme}
                  activeType={state.activeType}
                  item={item}
                  onClick={() => {
                    if (!item.href) {
                      setState((prevState) => ({
                        ...prevState,
                        activeType: prevState.activeType === item.type ? undefined : item.type,
                      }))
                    }
                  }}
                />
              ))}
            </ul>
          </div>
          <div className={twJoin("flex")}>
            <div className={twJoin("hidden", "480:flex items-center gap-x-16p mr-32p")}>
              {/*<Link
                href={useUtmTags("/demo", { utm_medium: Utm.MEDIUM.HEADER, utm_campaign: Utm.CAMPAIGN.LINK, utm_term: "Demo"})}
                className={twJoin(
                  "text-slate-blue-300 hover:text-slate-blue-200",
                  "[.light_&]:text-primary-violet",
                  "[.light_&]:hover:text-violet-500",
                  "whitespace-nowrap",
                  "text-14p leading-20p font-500"
                )}
              >
                Demo
              </Link>
              <span className={twJoin("border-r", "border-right", "border-dotted border-neutral-600 ", "w-1p h-16p")} />*/}
              <Link
                href={ useUtmTags(`${ process.env.NEXT_PUBLIC_APP_TRAQQ_URL }/login`, { utm_medium: Utm.MEDIUM.HEADER, utm_campaign: Utm.CAMPAIGN.BUTTON, utm_term: "Log In"})}
                className={twJoin(
                  "text-slate-blue-300 hover:text-slate-blue-200",
                  "[.light_&]:text-primary-violet",
                  "[.light_&]:hover:text-violet-500",
                  "whitespace-nowrap",
                  "text-14p leading-20p font-500"
                )}
              >
                Log in
              </Link>
            </div>

            <MainButton
                href={ useUtmTags(`${ process.env.NEXT_PUBLIC_APP_TRAQQ_URL }/signup`, { utm_medium: Utm.MEDIUM.HEADER, utm_campaign: Utm.CAMPAIGN.BUTTON, utm_term: "Free trial"})}
              size={36}
              size480={44}
            >
              <span className={twJoin("hidden", "480:block")}>Create workspace</span>
              <Icon
                name={"enter-icon"}
                color="neutral-900"
                size={20}
                className={twJoin("block", "480:!hidden")}
              />
            </MainButton>
            <MobileNavigation theme={getTheme} />
          </div>
        </div>
        {linkSource.map((item) => (
          <NavigationSubItems
            key={item.type}
            theme={getTheme}
            isVisible={state.activeType === item.type}
            type={item.type}
            closeAction={() => {
              setState((prevState) => ({
                ...prevState,
                activeType: undefined,
              }))
            }}
          />
        ))}
      </div>
    </nav>
  )
}
